/* eslint-disable max-len */
// ============================================================
// 非同步打卡 Firestore 監聽器（app 層）
// ------------------------------------------------------------
// 對稱 offlinePunchScheduler，但收的是「已交給 BE 非同步管線」的紀錄（asyncStatus = accepted / processing / published）：
// 對每筆的 docKey 做 Firestore 即時監聽，根據 doc 狀態收尾。
//
// 觸發：start 時做一次 sync；之後靠 offlinePunchQueue.subscribe（新 accepted / 被 remove）重新對齊監聽集合。
// 結果分流（classifyPunchTaskDoc）：
//   loading    → 持續等（按鈕維持互動鎖但不變灰，由 usePendingAsyncPunchKeys 反映）
//   success    → 押 punchedAt、標 succeeded，推 outcome（成功）；listener 會 getPunchInCall 後 remove
//   fail       → 推 outcome（失敗，帶 error_code / error_message）後 remove；首頁才開 modal
//   republish  → publish_failed：產新 idempotency_key 重打 /enqueue（前景不帶 punch_datetime），
//                accepted 用 guarded transaction 換 docKey 續聽 / 5xx 用單筆 transaction 轉 retry / 4xx 比照 fail 收尾
//
// UI feedback 全部走既有 pushPunchInRetryOutcome → usePunchInRetryOutcomeListener，與 local queue 同一條鏈。
//
// 註：實際 onSnapshot 由 firestoreWatcherAdapter 注入；未注入時是 no-op stub（結果不會回來），
//     _app 動態 import 完成後會呼叫 resync() 把 stub 期間掛上的監聽全拆重建。
// ============================================================

import { Store } from 'redux';

import * as offlinePunchQueue from '@src/db/offlinePunchQueue';
import * as offlinePunchScheduler from '@src/utils/punch/offlinePunchScheduler';
import { pushPunchInRetryOutcome } from '@src/store/actions/punchIn';
import * as selectors from '@src/store/selectors/user';
import { getPunchTaskCollection, watchPunchTaskDoc } from '@src/utils/punch/firestoreWatcherAdapter';
import { classifyPunchTaskDoc, PunchTaskDoc } from '@src/utils/punch/punchTaskStatus';
import { callPunchQueue, buildQueuePayload, newIdempotencyKey, parseEnqueueCreatedAt, PUNCH_API_TIMEOUT_MS } from '@src/utils/punch/punchQueueClient';

import { getMachinePunchResult } from '@src/utils/punch/machinePunchData';
import { isDeviceAbnormalErrorType } from '@src/utils/punch/deviceAbnormality';

type Unsub = () => void;
// 吞掉非關鍵的 IDB 讀寫失敗（下次 sync / TTL 會兜底）
const noop: Unsub = () => { /* swallow */ };

let currentAccountId: string | null = null;
let storeRef: Store | null = null;
// 每次 stop/start 都換 generation；舊帳號的 in-flight await 回來後不可把 outcome
// dispatch 到新帳號的 Redux/UI，也不可在換帳號後才送新的 republish HTTP。
let lifecycleGeneration = 0;
let queueUnsub: Unsub | null = null;
// `${collection}/${docKey}` -> Firestore unsubscribe（一般／卡機 task 分屬不同 collection）
const watching = new Map<string, Unsub>();
// 處理中的 recordId：擋同筆紀錄的 snapshot 重入（resync / watch-error 重掛會重播 initial snapshot），
// 避免 republish 在飛時又收到一次 publish_failed → 產兩把新 key 重打兩筆
const handling = new Set<string>();
// 處理中被擋下的最新一筆事件，處理完補放（只重播 doc 終態會停止更新，直接丟棄會卡 outstanding 到下次進頁）
const pendingReplay = new Map<string, {
	docKey: string;
	doc: PunchTaskDoc | null;
	allowAlreadyTerminal: boolean;
}>();
let isSyncing = false;
let syncRunSequence = 0;
let activeSyncRunId: number | null = null;
// sync 進行中又被觸發時記 dirty，結束後再跑一輪，避免漏掉期間的 queue 事件（scheduler 有 timer 兜底，這裡沒有）
let syncQueued = false;
let visibilityHandler: (() => void) | null = null;
let syncRetryTimer: ReturnType<typeof setTimeout> | null = null;
let syncRetryAttempt = 0;

const REPUBLISH_CONFIGS = {
	timeout: PUNCH_API_TIMEOUT_MS,
	suppressErrorToast: true,
};

// 監聽掛掉（onError）後延遲重建的間隔：拉開距離避免 permission-denied 之類「立刻再錯」的錯誤形成緊迴圈
const WATCH_ERROR_RESYNC_DELAY_MS = 30 * 1000;

// terminal snapshot 只會 emit 一次；local transition unknown 時必須重播同一份 doc。delay 封頂避免
// 持續 IDB failure 形成 hot loop，同時保證 storage 恢復後仍能完成且只推一次 outcome。
const TERMINAL_RETRY_BACKOFF_MS = [1000, 5000, 15_000, 30_000];
const terminalRetryTimers = new Map<string, {
	timer: ReturnType<typeof setTimeout> | null;
	attempt: number;
	accountId: string;
	docKey: string;
	doc: PunchTaskDoc | null;
}>();

function clearSyncRetry(): void {
	if (syncRetryTimer) clearTimeout(syncRetryTimer);
	syncRetryTimer = null;
	syncRetryAttempt = 0;
}

function scheduleSyncRetry(): void {
	if (!currentAccountId || syncRetryTimer) return;
	const delay = TERMINAL_RETRY_BACKOFF_MS[
		Math.min(syncRetryAttempt, TERMINAL_RETRY_BACKOFF_MS.length - 1)
	];
	syncRetryAttempt += 1;
	syncRetryTimer = setTimeout(() => {
		syncRetryTimer = null;
		sync();
	}, delay);
}

function clearTerminalRetry(recordId: string, docKey?: string): void {
	const state = terminalRetryTimers.get(recordId);
	if (!state || (docKey != null && state.docKey !== docKey)) return;
	if (state.timer) clearTimeout(state.timer);
	terminalRetryTimers['delete'](recordId);
}

function scheduleTerminalRetry(
	recordId: string,
	docKey: string,
	doc: PunchTaskDoc | null,
	accountId: string,
): void {
	const prior = terminalRetryTimers.get(recordId);
	if (prior?.timer) clearTimeout(prior.timer);
	const attempt = prior?.docKey === docKey && prior.accountId === accountId ? prior.attempt : 0;
	// 離開 /hr 只暫停 replay，不丟 snapshot。local terminal commit 若已成功但 read-back
	// 暫敗，record 已不在 outstanding；回到同帳號時必須靠這份 app-lifetime state 補推 outcome。
	if (currentAccountId !== accountId) {
		terminalRetryTimers.set(recordId, { timer: null, attempt, accountId, docKey, doc });
		return;
	}
	const delay = TERMINAL_RETRY_BACKOFF_MS[Math.min(attempt, TERMINAL_RETRY_BACKOFF_MS.length - 1)];
	const timer = setTimeout(() => {
		const current = terminalRetryTimers.get(recordId);
		if (!current || current.docKey !== docKey || current.accountId !== accountId) return;
		terminalRetryTimers.set(recordId, { ...current, timer: null, attempt: attempt + 1 });
		if (currentAccountId !== accountId) return;
		handleDoc(recordId, docKey, doc, true)['catch'](() => {
			scheduleTerminalRetry(recordId, docKey, doc, accountId);
		});
	}, delay);
	terminalRetryTimers.set(recordId, { timer, attempt, accountId, docKey, doc });
}

// lease winner 分頁若在 HTTP / local transition 途中關閉，其他 tab 在 lease 到期後重播同一個
// publish_failed snapshot。claimRepublish 會沿用 persisted next key，因此接手不會建第二個 task。
const republishLeaseTimers = new Map<string, ReturnType<typeof setTimeout>>();

function clearRepublishLeaseTimer(recordId: string): void {
	const timer = republishLeaseTimers.get(recordId);
	if (timer) clearTimeout(timer);
	republishLeaseTimers['delete'](recordId);
}

function scheduleRepublishAfterLease(
	recordId: string,
	docKey: string,
	doc: PunchTaskDoc | null,
	leaseExpiresAt: number,
): void {
	clearRepublishLeaseTimer(recordId);
	const delay = Math.max(0, leaseExpiresAt - Date.now() + 1);
	const timer = setTimeout(() => {
		republishLeaseTimers['delete'](recordId);
		handleDoc(recordId, docKey, doc).then(
			() => { sync(); offlinePunchScheduler.kick(); },
			() => { sync(); offlinePunchScheduler.kick(); },
		);
	}, delay);
	republishLeaseTimers.set(recordId, timer);
}

const getRetryContext = (record: offlinePunchQueue.RegularQueueRecord) => ({
	accountId: record.accountId,
	payload: record.payload,
	queueMeta: {
		displayLocationName: record.displayLocationName,
		displayShiftName: record.displayShiftName,
		allowPunchInOutOfRange: record.allowPunchInOutOfRange,
		rolloverSnapshot: record.rolloverSnapshot,
	},
});

async function claimRepublishDurably(
	recordId: string,
	options: Parameters<typeof offlinePunchQueue.claimRepublish>[1],
): Promise<offlinePunchQueue.ClaimRepublishResult | null> {
	try {
		return await offlinePunchQueue.claimRepublish(recordId, options);
	} catch {
		try {
			return await offlinePunchQueue.claimRepublish(recordId, options);
		} catch {
			// 兩次 completion 都 reject 時只 read-back，不憑空建立 claim、更不送 HTTP。
			const latest = await offlinePunchQueue.getById(recordId)['catch'](() => undefined);
			const lease = latest?.republishLease;
			const now = Date.now();
			const matchesSource = !!lease &&
				lease.sourceIdempotencyKey === options.source.idempotencyKey &&
				lease.sourceDocKey === options.source.docKey &&
				lease.sourceRetryCount === options.source.retryCount;
			if (
				latest && lease && matchesSource &&
					lease.ownerId === options.ownerId &&
					lease.expiresAt > now
			) {
				offlinePunchQueue.notifyQueueSubscribers();
				return { result: 'claimed', lease, record: latest };
			}
			if (lease && matchesSource && lease.expiresAt > now) {
				return { result: 'busy', leaseExpiresAt: lease.expiresAt };
			}
			// read-back 只看到 expired lease 時不可直接送 HTTP；必須再經 transaction renew，
			// claimRepublish 會保留 lease 內的 stable next key，並重新處理跨 tab race。
			if (lease && matchesSource) {
				try {
					return await offlinePunchQueue.claimRepublish(recordId, {
						...options,
						candidateNextIdempotencyKey: lease.nextIdempotencyKey,
					});
				} catch {
					const renewed = await offlinePunchQueue.getById(recordId)['catch'](() => undefined);
					const renewedLease = renewed?.republishLease;
					if (
						renewed && renewedLease &&
						renewedLease.ownerId === options.ownerId &&
						renewedLease.nextIdempotencyKey === lease.nextIdempotencyKey &&
						renewedLease.sourceIdempotencyKey === options.source.idempotencyKey &&
							renewedLease.sourceDocKey === options.source.docKey &&
							renewedLease.sourceRetryCount === options.source.retryCount &&
							renewedLease.expiresAt > Date.now()
					) {
						offlinePunchQueue.notifyQueueSubscribers();
						return { result: 'claimed', lease: renewedLease, record: renewed };
					}
					if (renewedLease && renewedLease.expiresAt > Date.now()) {
						return { result: 'busy', leaseExpiresAt: renewedLease.expiresAt };
					}
					return null;
				}
			}
			return null;
		}
	}
}

// IDB transaction 可能「已 commit 但 completion promise reject」；以相同 claim/key/doc
// 重試 local transition 或 read-back 確認，絕不重打 HTTP。
async function markRepublishedAcceptedDurably(
	recordId: string,
	options: Parameters<typeof offlinePunchQueue.markRepublishedAccepted>[1],
): Promise<Awaited<ReturnType<typeof offlinePunchQueue.markRepublishedAccepted>> | null> {
	try {
		return await offlinePunchQueue.markRepublishedAccepted(recordId, options);
	} catch {
		try {
			const retried = await offlinePunchQueue.markRepublishedAccepted(recordId, options);
			offlinePunchQueue.notifyQueueSubscribers();
			return retried;
		} catch {
			const latest = await offlinePunchQueue.getById(recordId)['catch'](() => undefined);
			if (!offlinePunchQueue.isRepublishedAcceptedTransitionApplied(latest, options)) return null;
			offlinePunchQueue.notifyQueueSubscribers();
			return 'updated';
		}
	}
}

async function transitionRepublishFailure(
	recordId: string,
	options: Parameters<typeof offlinePunchQueue.transitionRepublishRetryFailure>[1],
): Promise<Awaited<ReturnType<typeof offlinePunchQueue.transitionRepublishRetryFailure>> | null> {
	try {
		return await offlinePunchQueue.transitionRepublishRetryFailure(recordId, options);
	} catch {
		try {
			const retried = await offlinePunchQueue.transitionRepublishRetryFailure(recordId, options);
			offlinePunchQueue.notifyQueueSubscribers();
			return retried;
		} catch {
			const latest = await offlinePunchQueue.getById(recordId)['catch'](() => undefined);
			const applied = offlinePunchQueue.getAppliedRepublishRetryResult(latest, options);
			if (!applied) return null;
			offlinePunchQueue.notifyQueueSubscribers();
			return applied;
		}
	}
}

async function finalizeRepublishFatalDurably(
	recordId: string,
	options: Parameters<typeof offlinePunchQueue.finalizeRepublishFatal>[1],
): Promise<Awaited<ReturnType<typeof offlinePunchQueue.finalizeRepublishFatal>> | null> {
	try {
		return await offlinePunchQueue.finalizeRepublishFatal(recordId, options);
	} catch {
		try {
			const retried = await offlinePunchQueue.finalizeRepublishFatal(recordId, options);
			offlinePunchQueue.notifyQueueSubscribers();
			// not_found 只代表「現在不存在」，不能證明是這個舊 callback 的第一次 delete
			// commit；另一 owner 可能已 terminal-delete。缺 tombstone 時寧可不推 stale outcome。
			return retried;
		} catch {
			try {
				const latest = await offlinePunchQueue.getById(recordId);
				if (latest) return null;
				offlinePunchQueue.notifyQueueSubscribers();
				return null;
			} catch {
				// read 本身 reject 不等於 not_found；保持 lease 並交給 expiry timer 重跑。
				return null;
			}
		}
	}
}

async function processDoc(
	recordId: string,
	docKey: string,
	doc: PunchTaskDoc | null,
	allowAlreadyTerminal = false,
): Promise<void> {
	const accountId = currentAccountId;
	const generation = lifecycleGeneration;
	const outcomeStore = storeRef;
	const isCurrentLifecycle = (): boolean => (
		accountId !== null &&
		currentAccountId === accountId &&
		lifecycleGeneration === generation &&
		storeRef === outcomeStore
	);
	const isAccountStillActive = (): boolean => (
		accountId !== null &&
		!!outcomeStore &&
		selectors.getUserId(outcomeStore.getState()) === accountId
	);
	// 重新讀現值：監聽 closure 裡的 record 是訂閱當下的快照，可能過期；也用來去重（已收尾就略過）
	const action = classifyPunchTaskDoc(doc);
	let record: offlinePunchQueue.LocalQueueRecord | undefined;
	try {
		record = await offlinePunchQueue.getById(recordId);
	} catch {
		// 終態可能只 emit 一次，讀取失敗也要保留 snapshot；sync 不會重掛仍在 watching
		// 的 doc。沿用 bounded replay；已 stop/start 的舊 read 交給新訂閱，不污染新 lifecycle。
		if (isCurrentLifecycle() && accountId && action.type !== 'loading') {
			scheduleTerminalRetry(recordId, docKey, doc, accountId);
		}
		return;
	}
	if (!isCurrentLifecycle()) return;
	if (!record || record.accountId !== accountId) {
		clearTerminalRetry(recordId, docKey);
		return;
	}
	// stale watcher（republish 換 docKey 後、sync 拆掉舊監聽前）的事件，略過
	if (record.docKey !== docKey) {
		clearTerminalRetry(recordId, docKey);
		return;
	}
	if (!allowAlreadyTerminal && (record.asyncStatus === 'succeeded' || record.asyncStatus === 'failed')) return;
	if (!record.idempotencyKey) return;
	const source: offlinePunchQueue.AsyncDocumentSource = {
		idempotencyKey: record.idempotencyKey,
		docKey,
		retryCount: record.retryCount,
	};

	const allowModal = record.allowResultModal !== false;

	switch (action.type) {
	case 'loading':
		if (doc?.status && doc.status !== record.asyncStatus) {
			await offlinePunchQueue.updateAsyncDocumentStatus(recordId, source, doc.status)['catch'](noop);
		}
		break;

	case 'success': {
		let machineResult = record.punchKind === 'machine' ? record.machineResult : undefined;
		if (record.punchKind === 'machine' && !machineResult) {
			try {
				if (!doc?.punch_in_record_id || !isAccountStillActive()) throw new Error('Missing machine result ID');
				machineResult = await getMachinePunchResult(doc.punch_in_record_id, record.accountId, record.punchedAt ?? record.createdAt, outcomeStore?.getState()?.user?.profile?.timezone);
				if (!isCurrentLifecycle() || !isAccountStillActive()) return;
				if (!await offlinePunchQueue.saveMachineResult(recordId, source, machineResult)) return;
			} catch {
				if (isCurrentLifecycle()) scheduleTerminalRetry(recordId, docKey, doc, record.accountId);
				return;
			}
		}
		// 押 punchedAt 驅動 optimistic（避免 getPunchInCall 回來前按鈕閃回未打卡），標 succeeded 讓 sync 取消監聽
		const finalized = await offlinePunchQueue.markAsyncSucceededDurably(recordId, source);
		if (finalized === 'updated' || finalized === 'already_applied') {
			clearTerminalRetry(recordId, docKey);
			outcomeStore?.dispatch(pushPunchInRetryOutcome({
				id: recordId,
				accountId: record.accountId,
				success: true,
				machineResult,
				payload: record.punchKind === 'machine' ? undefined : record.payload,
				...(record.punchKind === 'machine' ? { punchKind: 'machine' as const } : {}),
				allowModal,
			}));
		} else if (finalized === 'unknown') scheduleTerminalRetry(recordId, docKey, doc, record.accountId);
		else clearTerminalRetry(recordId, docKey);
		break;
	}

	case 'fail': {
		const finalized = await offlinePunchQueue.markAsyncFailedDurably(recordId, source);
		if (finalized === 'unknown') {
			scheduleTerminalRetry(recordId, docKey, doc, record.accountId);
			break;
		}
		if (finalized !== 'updated' && finalized !== 'already_applied') {
			clearTerminalRetry(recordId, docKey);
			break;
		}
		clearTerminalRetry(recordId, docKey);
		// Worker 可能以通用 error_code 搭配設備異常 error_message 回報，統一成 UI 使用的 errorType。
		const deviceErrorType = [action.errorCode, action.errorMessage].find(isDeviceAbnormalErrorType);
		outcomeStore?.dispatch(pushPunchInRetryOutcome({
			id: recordId,
			accountId: record.accountId,
			success: false,
			payload: record.punchKind === 'machine' ? undefined : record.payload,
			...(record.punchKind === 'machine' ? { punchKind: 'machine' as const } : {}),
			allowModal,
			asyncError: {
				code: action.errorCode,
				message: action.errorMessage,
				...(deviceErrorType ? { errorType: deviceErrorType } : {}),
			},
			...(allowModal && record.punchKind !== 'machine' ? { retryContext: getRetryContext(record) } : {}),
		}));
		// 失敗不重送，直接刪。前景紀錄在點擊當下就押 punchedAt（CTA 已樂觀顯示打卡時間），
		// remove 後 optimistic 時間消失、CTA 退回未打卡——最終失敗即應如此，與落 queue 重送鏈的紀錄同行為
		await offlinePunchQueue.removeAsyncFailed(recordId, source)['catch'](noop);
		break;
	}

	case 'republish': {
		// read-failure replay 到此已恢復，後續 claim/HTTP 失敗由 persisted lease timer 接手。
		clearTerminalRetry(recordId, docKey);
		// 先用 IDB transaction claim lease，再送 HTTP。跨 tab 只有 lease winner 會送；
		// winner 中斷後，接手者沿用 lease 裡持久化的 next key。
		const ownerId = newIdempotencyKey();
		const claim = await claimRepublishDurably(recordId, {
			source,
			ownerId,
			candidateNextIdempotencyKey: newIdempotencyKey(),
		});
		if (!claim) {
			scheduleRepublishAfterLease(
				recordId,
				docKey,
				doc,
				Date.now() + offlinePunchQueue.REPUBLISH_LEASE_MS,
			);
			break;
		}
		if (claim.result === 'busy') {
			scheduleRepublishAfterLease(recordId, docKey, doc, claim.leaseExpiresAt);
			break;
		}
		if (claim.result !== 'claimed') break;
		clearRepublishLeaseTimer(recordId);
		// claim transaction 之後才準備送 HTTP；若期間已切帳號，保留 persisted lease
		// 給原帳號下次在 expiry 後接手，不能拿新帳號 auth 送 A 的 payload。
		if (!isAccountStillActive()) break;
		const claimedRecord = claim.record;
		const newKey = claim.lease.nextIdempotencyKey;
		const isForeground = claimedRecord.allowResultModal !== false;
		// 前景第一次 202 會把 punchedAt 換成 server time，但 republish 仍失敗後正式進
		// local retry 時，AC2 要求回到使用者點擊時間 createdAt。queue-origin 則保留原補登時間。
		const retryPunchedAtMs = isForeground ?
			claimedRecord.createdAt : claimedRecord.punchedAt ?? claimedRecord.createdAt;
		const result = await callPunchQueue(
			isForeground ?
				claimedRecord.payload :
				buildQueuePayload(claimedRecord.payload, retryPunchedAtMs, claimedRecord.deferredReason),
			newKey,
			REPUBLISH_CONFIGS,
			claimedRecord.punchKind,
		);
		if (result.kind === 'accepted') {
			const republishedAt = isForeground ? parseEnqueueCreatedAt(result.task.document_created_at) : null;
			const acceptedTransition = await markRepublishedAcceptedDurably(recordId, {
				source,
				nextIdempotencyKey: newKey,
				nextDocKey: result.task.document_id,
				leaseOwnerId: claim.lease.ownerId,
				...(republishedAt !== null ? { punchedAt: republishedAt } : {}),
			});
			if (acceptedTransition === null) {
				scheduleRepublishAfterLease(recordId, docKey, doc, claim.lease.expiresAt);
			}
		} else if (result.kind === 'retryable') {
			// 以單一 transaction 同時換 key、清 ownership、寫 retry metadata 與 backoff；
			// scheduler 不會觀察到 enterRetryQueue 和 markFailedRetry 之間的半成品 pending 狀態。
			const transition = await transitionRepublishFailure(recordId, {
				source,
				nextIdempotencyKey: newKey,
				leaseOwnerId: claim.lease.ownerId,
				deferredReason: result.deferredReason,
				lastError: result.deferredReason,
				punchedAt: retryPunchedAtMs,
			});
			if (transition === 'exhausted') {
				outcomeStore?.dispatch(pushPunchInRetryOutcome({
					id: recordId,
					accountId: record.accountId,
					success: false,
					payload: record.punchKind === 'machine' ? undefined : record.payload,
					...(record.punchKind === 'machine' ? { punchKind: 'machine' as const } : {}),
					allowModal: false,
				}));
			}
			// 兩次 transition reject 且 read-back 也失敗時仍 kick：若 commit 其實成功，scheduler
			// 會接手 pending；若未成功，lease timer 之後用同 key 重試 republish。
			if (transition === 'scheduled' || transition === null) offlinePunchScheduler.kick();
			if (transition === null) {
				scheduleRepublishAfterLease(recordId, docKey, doc, claim.lease.expiresAt);
			}
		} else {
			// fatal（4xx）也用 claim guard 原子刪除；lease 已被新 tab 接手時，舊 callback
			// 不可 remove 新 owner 的紀錄或重複推失敗 outcome。
			const finalized = await finalizeRepublishFatalDurably(recordId, {
				source,
				nextIdempotencyKey: newKey,
				leaseOwnerId: claim.lease.ownerId,
			});
			if (finalized === 'removed') {
				outcomeStore?.dispatch(pushPunchInRetryOutcome({
					id: recordId,
					accountId: claimedRecord.accountId,
					success: false,
					payload: claimedRecord.punchKind === 'machine' ? undefined : claimedRecord.payload,
					...(claimedRecord.punchKind === 'machine' ? { punchKind: 'machine' as const } : {}),
					allowModal,
					asyncError: { message: result.response?.data?.raw, errorType: result.response?.data?.error_type },
					...(allowModal && claimedRecord.punchKind !== 'machine' ? { retryContext: getRetryContext(claimedRecord) } : {}),
				}));
			}
			if (finalized === null) {
				scheduleRepublishAfterLease(recordId, docKey, doc, claim.lease.expiresAt);
			}
		}
		break;
	}

	default:
		break;
	}

	if (isCurrentLifecycle()) sync();
}

// snapshot 進入點：同筆紀錄處理中（如 republish 的 /enqueue 在飛）先記下事件、處理完補放，
// 不直接丟棄——doc 到終態後不會再有 snapshot，丟掉終態事件會讓紀錄卡在 outstanding。
// 補放不會讓 republish 重播疑慮回來：republish 成功後 record.docKey 已換新，舊事件被 processDoc 的 stale-docKey 檢查擋掉
async function handleDoc(
	recordId: string,
	docKey: string,
	doc: PunchTaskDoc | null,
	allowAlreadyTerminal = false,
): Promise<void> {
	if (handling.has(recordId)) {
		const pending = pendingReplay.get(recordId);
		pendingReplay.set(recordId, {
			docKey,
			doc,
			allowAlreadyTerminal: allowAlreadyTerminal || pending?.allowAlreadyTerminal === true,
		}); // 只留最新一筆，舊的被覆蓋正好
		return;
	}
	handling.add(recordId);
	try {
		await processDoc(recordId, docKey, doc, allowAlreadyTerminal);
	} finally {
		handling['delete'](recordId);
		const replay = pendingReplay.get(recordId);
		if (replay) {
			pendingReplay['delete'](recordId);
			handleDoc(recordId, replay.docKey, replay.doc, replay.allowAlreadyTerminal)['catch'](noop);
		}
	}
}

async function sync(): Promise<void> {
	if (!currentAccountId) return;
	if (isSyncing) {
		syncQueued = true;
		return;
	}
	isSyncing = true;
	const accountId = currentAccountId;
	const generation = lifecycleGeneration;
	const syncStore = storeRef;
	const runId = syncRunSequence + 1;
	syncRunSequence = runId;
	activeSyncRunId = runId;
	const isCurrentSyncLifecycle = (): boolean => (
		currentAccountId === accountId &&
		lifecycleGeneration === generation &&
		storeRef === syncStore
	);
	try {
		const outstanding = await offlinePunchQueue.getOutstandingAsync(accountId);
		if (!isCurrentSyncLifecycle()) return;
		const getWatchKey = (record: offlinePunchQueue.LocalQueueRecord): string => (
			`${getPunchTaskCollection(record.punchKind)}/${record.docKey as string}`
		);
		const wantKeys = new Set(outstanding.map(getWatchKey));

		// 取消不再 outstanding（已收尾 / 被 remove）的監聽
		Array.from(watching.keys()).forEach(key => {
			if (!wantKeys.has(key)) {
				watching.get(key)?.();
				watching['delete'](key);
			}
		});

		// 對新的 outstanding 紀錄建立監聽
		outstanding.forEach(record => {
			const key = getWatchKey(record);
			const docKey = record.docKey as string;
			if (watching.has(key)) return;
			const unsub = watchPunchTaskDoc(
				docKey,
				doc => { handleDoc(record.id, docKey, doc)['catch'](noop); },
				() => {
					if (!isCurrentSyncLifecycle()) return;
					// Firestore 的 error callback = 監聽已終止、不會再送 snapshot（暫時性網路錯誤 SDK 內部重試、不進這裡）。
					// 清掉佔位讓 sync 能重建；延遲重試避免「自己排的重建」形成緊迴圈。stop 後 sync 會自行 no-op
					watching['delete'](key);
					setTimeout(() => { sync(); }, WATCH_ERROR_RESYNC_DELAY_MS);
				},
				getPunchTaskCollection(record.punchKind),
			);
			watching.set(key, unsub);
		});
		clearSyncRetry();
	} catch (e) {
		// IDB 讀取失敗（隱私模式 / storage 被回收）→ 本輪放棄，不讓 fire-and-forget 呼叫端變 unhandled rejection；
		// 用封頂 backoff 自我恢復，不依賴下一個 snapshot / queue event。
		if (isCurrentSyncLifecycle()) scheduleSyncRetry();
	} finally {
		// stop/start 可讓同 account 的新 generation 在舊 IDB read 未完成時先跑；
		// 舊 run 不得清掉新 run 的 mutex，也不得消化新 generation 的 dirty flag。
		if (activeSyncRunId === runId) {
			activeSyncRunId = null;
			isSyncing = false;
			if (syncQueued) {
				syncQueued = false;
				sync();
			}
		}
	}
}

// 同 accountId 重複呼叫 no-op；不同 accountId 先 stop 再 start。
export function start(accountId: string, store: Store): void {
	if (currentAccountId === accountId && storeRef === store) return;
	stop();
	currentAccountId = accountId;
	storeRef = store;
	terminalRetryTimers.forEach((state, recordId) => {
		if (state.accountId === accountId && !state.timer) {
			scheduleTerminalRetry(recordId, state.docKey, state.doc, accountId);
		}
	});
	queueUnsub = offlinePunchQueue.subscribe(() => { sync(); });
	visibilityHandler = () => {
		if (typeof document !== 'undefined' && document.visibilityState === 'visible') sync();
	};
	if (typeof document !== 'undefined') {
		document.addEventListener('visibilitychange', visibilityHandler);
	}
	sync();
}

// 給 _app 在動態注入真 adapter 後呼叫：start 的 sync 可能先跑、把監聽掛在 no-op stub 上（永遠收不到結果），
// 全拆重建讓既有 outstanding 紀錄改掛真監聽。未 start 時 sync() 自行 no-op。
export function resync(): void {
	watching.forEach(unsub => unsub());
	watching.clear();
	sync();
}

export function stop(): void {
	lifecycleGeneration += 1;
	if (visibilityHandler && typeof document !== 'undefined') {
		document.removeEventListener('visibilitychange', visibilityHandler);
	}
	visibilityHandler = null;
	clearSyncRetry();
	watching.forEach(unsub => unsub());
	watching.clear();
	republishLeaseTimers.forEach(timer => clearTimeout(timer));
	republishLeaseTimers.clear();
	terminalRetryTimers.forEach((state, recordId) => {
		if (state.timer) clearTimeout(state.timer);
		terminalRetryTimers.set(recordId, { ...state, timer: null });
	});
	if (queueUnsub) { queueUnsub(); queueUnsub = null; }
	currentAccountId = null;
	storeRef = null;
	activeSyncRunId = null;
	isSyncing = false;
	syncQueued = false;
}
